import { useEffect, useRef, useState } from "react";
import { Redirect, useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { getAuth, isSignInWithEmailLink } from "firebase/auth";
import { useAtomValue, useSetAtom } from "jotai/react";
import { useForm } from "react-hook-form";
import { toast } from "sonner-native";

import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { VnTextInput } from "@/components/VnTextInput/VnTextInput";
import { useSignInWithEmailLinkMutation } from "@/data/mutations/authMutations";
import { useGetShortCodeDataQuery } from "@/data/queries/verifyApiQueries";
import { requiredEmailFormSchema, RequiredEmailFormType } from "@/domain/auth";
import { useTranslator } from "@/hooks/useTranslate";
import {
  emailForSigninAtom,
  mfaResolverAtom,
  onboardingShortCodeDataAtom,
} from "@/stores/atoms";
import { handleMfaError } from "@/utils/mfa";

type VerifySignInWithEmailLinkScreenPropsType = {
  code: string;
};

export const VerifySignInWithEmailLinkScreen = ({
  code,
}: VerifySignInWithEmailLinkScreenPropsType) => {
  const { t } = useTranslator();
  const router = useRouter();
  const auth = getAuth();

  const emailForSignin = useAtomValue(emailForSigninAtom);
  const setMfaResolver = useSetAtom(mfaResolverAtom);
  const setOnboardingShortCodeData = useSetAtom(onboardingShortCodeDataAtom);

  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [isRequestingUserEmail, setIsRequestingUserEmail] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasAttemptedSignIn = useRef(false);

  const emailPromptForm = useForm<RequiredEmailFormType>({
    mode: "onChange",
    defaultValues: {
      email: "",
    },
    resolver: standardSchemaResolver(requiredEmailFormSchema),
  });

  const { mutate: signInWithEmailLink } = useSignInWithEmailLinkMutation();
  const { data: shortCodeData } = useGetShortCodeDataQuery(code ?? "");

  const redirectWithError = () => {
    toast.error(t("common:verifySignInWithEmailLinkScreen.verifyError"));
    setShouldRedirect(true);
  };

  const handleEmailSignin = (email: string) => {
    setIsSubmitting(true);
    signInWithEmailLink(email, {
      onError: error => {
        setIsSubmitting(false);
        if (
          handleMfaError(error, resolver => {
            setMfaResolver(resolver);
            router.push("/(unauthed)/mfa-verify");
          })
        )
          return;
        redirectWithError();
      },
    });
  };

  const handleEmailSubmit = (data: RequiredEmailFormType) => {
    handleEmailSignin(data.email);
  };

  // When a short code is provided via the email link, hydrate the atom with its data.
  // This ensures that downstream flows (MFA enrollment) have the short code context
  // even if the user opened the link in a different browser/tab.
  useEffect(() => {
    if (shortCodeData) {
      setOnboardingShortCodeData(shortCodeData);
    }
  }, [shortCodeData, setOnboardingShortCodeData]);

  useEffect(() => {
    if (hasAttemptedSignIn.current) return;

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      redirectWithError();
      return;
    }

    const email = emailForSignin;

    if (!email) {
      hasAttemptedSignIn.current = true;
      setIsRequestingUserEmail(true);
      return;
    }

    // If a short code was provided, wait for it to load before signing in.
    // This ensures the atom is hydrated with short code data before downstream flows run.
    // If no code was provided, proceed directly with sign-in.
    if (code && shortCodeData === undefined) {
      // Still loading the short code
      return;
    }

    hasAttemptedSignIn.current = true;
    handleEmailSignin(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailForSignin, code, shortCodeData]);

  if (shouldRedirect) {
    return <Redirect href="/(unauthed)/auth" />;
  }

  return (
    <div className="container m-auto flex flex-col gap-6 text-center">
      <div className="mx-auto flex w-full max-w-sm flex-col items-center gap-2 px-4">
        {isRequestingUserEmail ? (
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                i18nKey="common:verifySignInWithEmailLinkScreen.confirmEmailPrompt"
                className="text-center"
              />
            </VnBox>
            <VnForm form={emailPromptForm} isInputOnlyForm>
              <VnCard>
                <VnTextInput
                  control={emailPromptForm.control}
                  name="email"
                  labelI18nKey="native:authScreen.emailInput"
                  placeholder={t("common:login.placeholder")}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  textContentType="emailAddress"
                  autoComplete="email"
                  returnKeyType="done"
                  enablesReturnKeyAutomatically
                  onSubmitEditing={() => {
                    void emailPromptForm.handleSubmit(handleEmailSubmit)();
                  }}
                />
                <VnButton
                  onPress={() => {
                    void emailPromptForm.handleSubmit(handleEmailSubmit)();
                  }}
                  i18nKey="common:verifySignInWithEmailLinkScreen.verify"
                  isLoading={isSubmitting}
                  className="rounded-md"
                  isDisabled={!emailPromptForm.formState.isValid}
                />
              </VnCard>
            </VnForm>
          </VnBox>
        ) : (
          <VnLoadingSpinner />
        )}
      </div>
    </div>
  );
};
